import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { TID } from '../lib/testIds';
import HeroParticles from './HeroParticles';

const REELS = [
  'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/kouyj2ey_ueta%20vid%203.mp4',
  'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/77gv5jik_ueta%20vid%201%20trim.mp4',
  'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/b5wnwekn_ueta%20vid%202%20trim.mp4',
];

export default function Hero() {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const nextReel = () => setIdx((i) => (i + 1) % REELS.length);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.lenis) {
      window.lenis.scrollTo(el, { offset: -70, duration: 1.1 });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={TID.heroRoot}
      className="relative h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Background video — cycles through all short-form reels */}
      <motion.div style={{ scale }} className="absolute inset-0">
        <motion.video
          key={idx}
          className="absolute inset-0 h-full w-full object-cover"
          src={REELS[idx]}
          autoPlay
          muted
          playsInline
          preload="auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, ease: 'easeInOut' }}
          onEnded={nextReel}
          onLoadedData={(e) => {
            const p = e.currentTarget.play();
            if (p && p.catch) p.catch(() => {});
          }}
        />
      </motion.div>

      {/* Vignette + grain */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      <div className="grain" />

      {/* Floating neon particles (scroll-reactive) */}
      <HeroParticles progress={scrollYProgress} />

      {/* Top overline */}
      <motion.div
        style={{ opacity }}
        className="absolute top-24 md:top-28 left-6 md:left-10 lg:left-14 z-10 flex flex-wrap items-center gap-x-3 gap-y-1 max-w-[60%] md:max-w-none pr-4 text-[10px] md:text-xs uppercase tracking-[0.3em]"
      >
        <span className="h-px w-8 bg-[#D8FF3A]" />
        <span className="text-[#D8FF3A] neon-text-glow">● Visual Media</span>
        <span className="text-white/40">·</span>
        <span className="text-white/70">Newcastle · Sydney</span>
      </motion.div>

      {/* Right meta */}
      <motion.div
        style={{ opacity }}
        className="absolute top-24 md:top-28 right-6 md:right-10 lg:right-14 z-10 text-right text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/70"
      >
        <span className="text-[#D8FF3A]">Est. 2026</span>
      </motion.div>

      {/* Title block */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 z-10 flex flex-col justify-end pb-20 md:pb-24 lg:pb-28 px-6 md:px-10 lg:px-14"
      >
        <motion.p
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid={TID.heroSub}
          className="max-w-xl text-sm md:text-base text-white/70 mb-6 md:mb-8 font-light"
        >
          A visual content studio crafting cinematic stories for brands, athletes, weddings and everything in between.
        </motion.p>

        <h1
          data-testid={TID.heroTitle}
          className="font-display font-black uppercase text-white leading-[0.82] tracking-[-0.04em] break-words"
          style={{ fontSize: 'clamp(44px, 15vw, 280px)' }}
        >
          <motion.span
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="block overflow-hidden"
          >
            <span className="block">UTMEDIA</span>
          </motion.span>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-wrap items-end justify-between gap-6">
          <motion.button
            data-testid={TID.heroCtaWork}
            onClick={() => go('reels')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.25em] text-white"
          >
            <span className="relative h-12 w-12 rounded-full border border-[#D8FF3A]/60 flex items-center justify-center transition-all duration-500 group-hover:bg-[#D8FF3A] group-hover:text-black group-hover:neon-glow text-[#D8FF3A] group-hover:text-black">
              <span aria-hidden className="translate-y-px">↓</span>
            </span>
            <span className="border-b border-white/30 pb-1 group-hover:border-[#D8FF3A] transition-colors">View the reel</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="text-right text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/50 max-w-[14rem]"
          >
            Brand · Sport · Wedding · Music
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom hairline */}
      <div className="absolute bottom-0 inset-x-0 hairline z-10" />
    </section>
  );
}
